import React, { useState, useEffect } from "react";
import { Dialog, Button, Flex, Table, Text } from "@fluentui/react-northstar";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { api } from "../../../../Assets/api/api";
import SchoolBranchForm from "./SchoolBranchForm";
import { Box, Popover, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Input, Checkbox } from "antd";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./BranchesManagement.css";

const BranchesManagement = ({ open, onCancel, user }) => {
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [deletingBranches, setDeletingBranches] = useState(new Set());
  const { t } = useTranslation();
  const translation = t("trustedNetwork").schools.branchesManagement;

  // Responsive breakpoints
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // 768px and below

  // Filter/search state
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterData, setFilterData] = useState({}); // Applied filters
  const [tempFilterData, setTempFilterData] = useState({}); // Temporary selections
  const [openDropdownIndex, setOpenDropdownIndex] = useState(-1);
  const [searchText, setSearchText] = useState({});
  const [filters, setFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredBranches, setFilteredBranches] = useState([]);

  const fetchBranches = async () => {
    setIsLoading(true);
    try {
      const slug = user.slug.replace(/^\?slug=/, "");
      const response = await api.get(`/api/school/get-branches?slug=${slug}`);
      // Only handle the observed structure: response.data.data is an array
      const branchesData = Array.isArray(response.data?.data)
        ? response.data.data
        : [];
      setBranches(branchesData);
    } catch (error) {
      console.error("Error fetching branches:", error);
      toast.error("Failed to fetch school branches");
      setBranches([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchBranches();
    }
  }, [open]);

  // Fetch filter options from backend
  useEffect(() => {
    if (open) {
      fetchFilterOptions();
    }
  }, [open]);

  const fetchFilterOptions = async () => {
    try {
      const slug = user.slug.replace(/^\?slug=/, "");
      const response = await api.get(
        `/api/school/get-branch-filter-options?slug=${slug}`
      );
      if (response.status === 200 && response.data.result) {
        const result = response.data.result;
        const backendFilters = Object.entries(result)
          .filter(([, arr]) => Array.isArray(arr) && arr.length)
          .map(([key, arr]) => ({
            id: key,
            title: key.charAt(0).toUpperCase() + key.slice(1),
            label: arr,
            selected: 0,
          }));
        setFilters(backendFilters);
        setSearchText(
          Object.fromEntries(backendFilters.map((f) => [f.id, ""]))
        );
      }
    } catch (error) {
      // Silently fail for now
      console.error("Error fetching filter options:", error);
      toast.error("Failed to fetch filter options");
    }
  };

  // Filtering logic
  useEffect(() => {
    let filtered = branches;
    // Apply search
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(
        (b) =>
          b.name?.toLowerCase().includes(q) ||
          b.email?.toLowerCase().includes(q) ||
          b.type?.toLowerCase().includes(q) ||
          b.category?.toLowerCase().includes(q) ||
          b.city?.toLowerCase().includes(q) ||
          b.country?.toLowerCase().includes(q)
      );
    }
    // Apply filters (case-insensitive)
    filters.forEach((filter) => {
      const selected = (filterData[filter.id] || []).map((v) =>
        v.toLowerCase()
      );
      if (selected.length > 0) {
        filtered = filtered.filter((b) =>
          selected.includes((b[filter.id] || "").toLowerCase())
        );
      }
    });
    setFilteredBranches(filtered);
  }, [branches, searchQuery, filterData, filters]);

  // Popover logic (copied from Groups)
  const useStyles = makeStyles(() => ({
    arrow: {
      "&:before": {
        border: "1px solid #E6E8ED",
      },
    },
    tooltip: {
      minWidth: 266,
      backgroundColor: "white",
      color: "black",
      padding: "10px",
      fontSize: "16px",
      borderRadius: 12,
      boxShadow:
        "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
      zIndex: "99999 !important",
    },
  }));
  const classes = useStyles();
  const handleDropdownToggle = (index) => {
    if (index !== openDropdownIndex) {
      const filterId = filters[index]?.id;
      if (filterId) {
        setSearchText((prev) => ({ ...prev, [filterId]: "" }));
      }
    }
    setOpenDropdownIndex(index === openDropdownIndex ? -1 : index);
  };
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenDropdownIndex(-1);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setOpenDropdownIndex(-1);
  };
  const toggle = (checked, val, filterId) => {
    setTempFilterData((prev) => {
      const newTempFilterData = { ...prev };
      if (!newTempFilterData[filterId]) {
        newTempFilterData[filterId] = [];
      }
      if (checked) {
        newTempFilterData[filterId] = [...newTempFilterData[filterId], val];
      } else {
        newTempFilterData[filterId] = newTempFilterData[filterId].filter(
          (v) => v !== val
        );
      }
      return newTempFilterData;
    });
  };

  // For small screens, use filterData directly instead of tempFilterData
  const toggleForSmallScreen = (checked, val, filterId) => {
    setFilterData((prev) => {
      const newFilterData = { ...prev };
      if (!newFilterData[filterId]) {
        newFilterData[filterId] = [];
      }
      if (checked) {
        newFilterData[filterId] = [...newFilterData[filterId], val];
      } else {
        newFilterData[filterId] = newFilterData[filterId].filter(
          (v) => v !== val
        );
      }
      return newFilterData;
    });
  };
  const openPopover = Boolean(anchorEl);
  const id = openPopover ? "branch-filter-popover" : undefined;

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setIsEditFormOpen(true);
  };

  const handleDelete = async (branchId) => {
    setDeletingBranches((prev) => new Set([...prev, branchId]));
    try {
      await api.delete(`/api/school/delete-branch/${branchId}${user.slug}`);
      toast.success("Branch deleted successfully!");
      fetchBranches(); // Refresh the list
    } catch (error) {
      console.error("Error deleting branch:", error);
      toast.error("Failed to delete branch");
    } finally {
      setDeletingBranches((prev) => {
        const next = new Set(prev);
        next.delete(branchId);
        return next;
      });
    }
  };

  const handleEditSuccess = () => {
    setIsEditFormOpen(false);
    setEditingBranch(null);
    fetchBranches(); // Refresh the list
  };

  const handleCreateSuccess = () => {
    setIsCreateFormOpen(false);
    fetchBranches(); // Refresh the list
  };

  return (
    <>
      <Dialog
        open={open}
        onCancel={onCancel}
        header={translation.dialogHeader}
        cancelButton={{
          content: translation.close,
        }}
        className="branches-management-dialog"
        style={{
          maxWidth: "1200px",
          width: "95vw",
          minWidth: "800px",
        }}
        content={
          <div style={{ padding: "10px 0", minHeight: "400px" }}>
            {/* Create Branch Button and Search/Filter UI */}
            <div className="branches-header-controls">
              <Button
                content={translation.createBranch}
                primary
                onClick={() => setIsCreateFormOpen(true)}
                style={{ backgroundColor: "#6264a7", color: "white" }}
              />
              <div className="search-filter-wrapper">
                <Input
                  placeholder={
                    translation?.searchBranchesPlaceholder ||
                    translation.searchBranchesPlaceholder
                  }
                  className="branch-search"
                  size="large"
                  allowClear
                  prefix={<SearchOutlined className="search-icon" />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ borderRadius: 8, width: 260 }}
                />
                <span
                  className="filter-button"
                  onClick={handleClick}
                >
                  <FilterOutlined />
                  <span>{translation.filter}</span>
                </span>
              </div>
            </div>

            {isLoading ? (
              <div className="branches-loading">
                <Text content={translation.loadingBranches} />
              </div>
            ) : !Array.isArray(branches) || branches.length === 0 ? (
              <div className="branches-empty">
                <Text content={translation.emptyState} />
              </div>
            ) : (
              <div>
                <div className="branches-count">
                  {translation.totalBranches || "Total Branches"}: {filteredBranches.length}
                </div>
                
                {/* Desktop Table View */}
                <div className="branches-table-container">
                  <table className="branches-table">
                    <thead>
                      <tr>
                        <th>{translation.tableHeaders.name}</th>
                        <th>{translation.tableHeaders.email}</th>
                        <th>{translation.tableHeaders.type}</th>
                        <th>{translation.tableHeaders.category}</th>
                        <th>{translation.tableHeaders.phone}</th>
                        <th>{translation.tableHeaders.city}</th>
                        <th>{translation.tableHeaders.country}</th>
                        <th>{translation.tableHeaders.actions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(filteredBranches) &&
                        filteredBranches.map((branch) => (
                          <tr key={branch.id}>
                            <td>{branch.name}</td>
                            <td>{branch.email || "-"}</td>
                            <td>{branch.type || "-"}</td>
                            <td>{branch.category || "-"}</td>
                            <td>{branch.contactPhone || "-"}</td>
                            <td>{branch.city || "-"}</td>
                            <td>{branch.country || "-"}</td>
                            <td>
                              <div className="branch-actions">
                                <Button
                                  icon={<EditOutlined />}
                                  size="small"
                                  className="branch-action-btn"
                                  onClick={() => handleEdit(branch)}
                                  title={translation.editBranch}
                                />
                                <Button
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  className="branch-action-btn"
                                  onClick={() => handleDelete(branch.id)}
                                  loading={deletingBranches.has(branch.id)}
                                  disabled={deletingBranches.has(branch.id)}
                                  title={translation.deleteBranch}
                                  style={{ color: "#d73527" }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                {Array.isArray(filteredBranches) &&
                  filteredBranches.map((branch) => (
                    <div key={`mobile-${branch.id}`} className="branches-mobile-card">
                      <div className="branches-mobile-card-header">
                        <h3 className="branches-mobile-card-title">{branch.name}</h3>
                        <div className="branches-mobile-card-actions">
                          <Button
                            icon={<EditOutlined />}
                            size="small"
                            className="branch-action-btn"
                            onClick={() => handleEdit(branch)}
                            title={translation.editBranch}
                          />
                          <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            className="branch-action-btn"
                            onClick={() => handleDelete(branch.id)}
                            loading={deletingBranches.has(branch.id)}
                            disabled={deletingBranches.has(branch.id)}
                            title={translation.deleteBranch}
                            style={{ color: "#d73527" }}
                          />
                        </div>
                      </div>
                      <div className="branches-mobile-card-content">
                        <div className="branches-mobile-card-row">
                          <span className="branches-mobile-card-label">{translation.tableHeaders.email}:</span>
                          <span className="branches-mobile-card-value">{branch.email || "-"}</span>
                        </div>
                        <div className="branches-mobile-card-row">
                          <span className="branches-mobile-card-label">{translation.tableHeaders.type}:</span>
                          <span className="branches-mobile-card-value">{branch.type || "-"}</span>
                        </div>
                        <div className="branches-mobile-card-row">
                          <span className="branches-mobile-card-label">{translation.tableHeaders.category}:</span>
                          <span className="branches-mobile-card-value">{branch.category || "-"}</span>
                        </div>
                        <div className="branches-mobile-card-row">
                          <span className="branches-mobile-card-label">{translation.tableHeaders.phone}:</span>
                          <span className="branches-mobile-card-value">{branch.contactPhone || "-"}</span>
                        </div>
                        <div className="branches-mobile-card-row">
                          <span className="branches-mobile-card-label">{translation.tableHeaders.city}:</span>
                          <span className="branches-mobile-card-value">{branch.city || "-"}</span>
                        </div>
                        <div className="branches-mobile-card-row">
                          <span className="branches-mobile-card-label">{translation.tableHeaders.country}:</span>
                          <span className="branches-mobile-card-value">{branch.country || "-"}</span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
            {/* Filter Popover */}
            <Popover
              id={id}
              open={openPopover}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              disablePortal={false}
              style={{ zIndex: 10000 }}
              PaperProps={{
                style: {
                  marginTop: "15px",
                  minWidth: "200px",
                  borderRadius: 12,
                  boxShadow:
                    "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
                  zIndex: 10000,
                },
              }}
            >
              {filters.map((data, index) => {
                const isOpenDropDown = index === openDropdownIndex;
                const filteredOptions = data.label.filter((o) => {
                  return (
                    (searchText[data.id] || "").length === 0 ||
                    o
                      .toLowerCase()
                      .includes((searchText[data.id] || "").toLowerCase())
                  );
                });
                const options =
                  (searchText[data.id] || "").length > 0
                    ? filteredOptions
                    : data.label;
                return (
                  <Box key={data.id}>
                    {isSmallScreen ? (
                      // Collapsible accordion for small screens
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            fontFamily: "Karla",
                            width: "100%",
                            padding: "12px 16px",
                            borderRadius: "8px",
                            backgroundColor: isOpenDropDown ? "#f5f5f5" : "transparent",
                            "&:hover": {
                              cursor: "pointer",
                              backgroundColor: "#f0f0f0",
                            },
                          }}
                          onClick={() => handleDropdownToggle(index)}
                        >
                          <Typography sx={{ flex: 1, fontFamily: "Karla", fontSize: "16px" }}>
                            {data.title}
                          </Typography>
                          {(filterData[data.id] || []).length > 0 && (
                            <Typography sx={{ 
                              backgroundColor: "#6264a7", 
                              color: "white", 
                              borderRadius: "12px", 
                              padding: "2px 8px", 
                              fontSize: "12px",
                              marginRight: "8px"
                            }}>
                              {(filterData[data.id] || []).length}
                            </Typography>
                          )}
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ 
                              transform: isOpenDropDown ? "rotate(90deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease"
                            }}
                          >
                            <path
                              d="M8.90961 20.67C8.71961 20.67 8.52961 20.6 8.37961 20.45C8.08961 20.16 8.08961 19.68 8.37961 19.39L14.8996 12.87C15.3796 12.39 15.3796 11.61 14.8996 11.13L8.37961 4.61002C8.08961 4.32002 8.08961 3.84002 8.37961 3.55002C8.66961 3.26002 9.14961 3.26002 9.43961 3.55002L15.9596 10.07C16.4696 10.58 16.7596 11.27 16.7596 12C16.7596 12.73 16.4796 13.42 15.9596 13.93L9.43961 20.45C9.28961 20.59 9.09961 20.67 8.90961 20.67Z"
                              fill="#292D32"
                            />
                          </svg>
                        </Box>
                        
                        {/* Collapsible content */}
                        {isOpenDropDown && (
                          <Box sx={{ 
                            padding: "8px 16px 16px 16px",
                            backgroundColor: "#fafafa",
                            borderRadius: "0 0 8px 8px",
                            maxHeight: "250px",
                            overflowY: "auto"
                          }}>
                            {/* Search bar */}
                            <input
                              type="search"
                              placeholder={`Search ${data.title}`}
                              style={{
                                width: "100%",
                                padding: "8px 12px",
                                border: "1px solid #d9d9d9",
                                borderRadius: "6px",
                                marginBottom: "12px",
                                fontSize: "14px"
                              }}
                              value={searchText[data.id] || ""}
                              onChange={(e) => {
                                const value = e?.target?.value ?? "";
                                setSearchText((prev) => ({
                                  ...prev,
                                  [data.id]: value,
                                }));
                              }}
                            />
                            
                            {/* Filter options */}
                            {data.label.length === 0 ? (
                              <Typography sx={{ color: "#666", fontSize: "14px", textAlign: "center", padding: "16px" }}>
                                {translation.noDataAvailable}
                              </Typography>
                            ) : options?.length > 0 ? (
                              options.map((label, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    padding: "8px 0",
                                    "&:hover": {
                                      backgroundColor: "#f0f0f0",
                                      borderRadius: "4px",
                                      padding: "8px 8px"
                                    }
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    id={`${data.id}-${label}`}
                                    value={label || ""}
                                    checked={(filterData[data.id] || []).includes(label)}
                                    onChange={(e) => {
                                      const checked = e?.target?.checked ?? false;
                                      const value = e?.target?.value ?? label;
                                      toggleForSmallScreen(checked, value, data.id);
                                    }}
                                    style={{ 
                                      width: "18px", 
                                      height: "18px",
                                      cursor: "pointer"
                                    }}
                                  />
                                  <label
                                    htmlFor={`${data.id}-${label}`}
                                    style={{ 
                                      fontSize: "14px", 
                                      cursor: "pointer",
                                      flex: 1
                                    }}
                                  >
                                    {label}
                                  </label>
                                </Box>
                              ))
                            ) : (
                              <Typography sx={{ color: "#666", fontSize: "14px", textAlign: "center", padding: "16px" }}>
                                {translation.notFound}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    ) : (
                      // Original tooltip for larger screens
                    <Tooltip
                      title={
                        <div
                          className={`overflow-auto collapse ${
                            isOpenDropDown ? "show" : ""
                          }`}
                          style={{ maxHeight: "300px", overflowY: "auto" }}
                          id={`${data.title}`}
                        >
                          {/* Always show search bar for each filter dropdown */}
                          <div className="mx-3 mt-3">
                            <input
                              type="search"
                              id={`search-${data.id}`}
                              name="search"
                              placeholder={`Search ${data.title}`}
                              className="search-box w-100"
                              value={searchText[data.id] || ""}
                              onChange={(e) => {
                                const value = e?.target?.value ?? "";
                                setSearchText((prev) => ({
                                  ...prev,
                                  [data.id]: value,
                                }));
                              }}
                            />
                          </div>
                          {data.label.length === 0 ? (
                            <div className="px-5 pt-3 pb-0">
                              <p>{translation.noDataAvailable}</p>
                            </div>
                          ) : options?.length > 0 ? (
                            options.map((label, idx) => (
                              <div
                                key={idx}
                                style={{
                                  display: "flex",
                                  gap: "10px",
                                  marginBottom: "5px",
                                  paddingLeft: "6px",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  id={`${data.id}-${label}`}
                                  value={label || ""}
                                  checked={(
                                    tempFilterData[data.id] || []
                                  ).includes(label)}
                                  onChange={(e) => {
                                    const checked = e?.target?.checked ?? false;
                                    const value = e?.target?.value ?? label;
                                    toggle(checked, value, data.id);
                                  }}
                                />
                                <label
                                  htmlFor={`${data.id}-${label}`}
                                  className="ms-3"
                                >
                                  {label}
                                </label>
                              </div>
                            ))
                          ) : (
                            <div className="px-5 pt-3 pb-0">
                              <p>{translation.notFound}</p>
                            </div>
                          )}
                        </div>
                      }
                      placement="left"
                      open={openDropdownIndex === index}
                      arrow
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      PopperProps={{
                        style: { zIndex: 99999 },
                        modifiers: [
                          {
                            name: "offset",
                            options: {
                              offset: [0, 10],
                            },
                          },
                        ],
                      }}
                      classes={{
                        arrow: classes.arrow,
                        tooltip: classes.tooltip,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          fontFamily: "Karla",
                          width: "96%",
                          margin: "4px",
                          borderRadius: "12px",
                          "&:hover": {
                            cursor: "pointer",
                            background: "#eeeeee",
                          },
                        }}
                      >
                        <Typography
                          onClick={() => {
                            handleDropdownToggle(index);
                            // When opening dropdown, initialize tempFilterData from filterData
                            if (openDropdownIndex !== index) {
                              setTempFilterData({ ...filterData });
                            }
                          }}
                          sx={{
                            fontFamily: "Karla",
                            padding: "3px 16px 3px 16px",
                            width: "96%",
                            margin: "4px",
                            borderRadius: "12px",
                            "&:hover": {
                              cursor: "pointer",
                              background: "#eeeeee",
                            },
                            textAlign: "start",
                          }}
                        >
                          {data.title}
                        </Typography>
                        {(filterData[data.id] || []).length > 0 && (
                          <Typography>
                            {(filterData[data.id] || []).length}
                          </Typography>
                        )}
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ marginRight: "5%" }}
                        >
                          <path
                            d="M8.90961 20.67C8.71961 20.67 8.52961 20.6 8.37961 20.45C8.08961 20.16 8.08961 19.68 8.37961 19.39L14.8996 12.87C15.3796 12.39 15.3796 11.61 14.8996 11.13L8.37961 4.61002C8.08961 4.32002 8.08961 3.84002 8.37961 3.55002C8.66961 3.26002 9.14961 3.26002 9.43961 3.55002L15.9596 10.07C16.4696 10.58 16.7596 11.27 16.7596 12C16.7596 12.73 16.4796 13.42 15.9596 13.93L9.43961 20.45C9.28961 20.59 9.09961 20.67 8.90961 20.67Z"
                            fill="#292D32"
                          />
                        </svg>
                      </Box>
                    </Tooltip>
                  )}
                  </Box>
                );
              })}
              {!isSmallScreen && (
                <div
                  style={{
                    padding: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    style={{
                      background: "#6264a7",
                      color: "white",
                      textTransform: "none",
                      padding: "0px 28px",
                      fontSize: "14px",
                      height: "30px",
                      fontFamily: "Karla",
                    }}
                    onClick={() => {
                      setFilterData({ ...tempFilterData });
                      handleClose();
                    }}
                  >
                    {translation.apply}
                  </Button>
                  <Button
                    style={{
                      textTransform: "none",
                      padding: "0px 28px",
                      fontSize: "14px",
                      height: "30px",
                      fontFamily: "Karla",
                      border: "transparent",
                    }}
                    onClick={() => {
                      setTempFilterData({});
                      setFilterData({});
                      setSearchText(
                        Object.fromEntries(filters.map((f) => [f.id, ""]))
                      );
                      handleClose();
                    }}
                  >
                    {translation.clear}
                  </Button>
                </div>
              )}
            </Popover>
          </div>
        }
      />

      {/* Create Branch Form */}
      <SchoolBranchForm
        open={isCreateFormOpen}
        onCancel={() => setIsCreateFormOpen(false)}
        user={user}
        onSuccess={handleCreateSuccess}
        isEditMode={false}
      />

      {/* Edit Branch Form */}
      <SchoolBranchForm
        open={isEditFormOpen}
        onCancel={() => {
          setIsEditFormOpen(false);
          setEditingBranch(null);
        }}
        user={user}
        onSuccess={handleEditSuccess}
        editingBranch={editingBranch}
        isEditMode={true}
      />
    </>
  );
};

export default BranchesManagement;
